// Client mirror of functions-common/src/charts/datasets/forms.ts. Keep in sync.

import type { DatasetDefinition } from '../types.js'

export const formsDataset: DatasetDefinition = {
  key: 'forms',
  labelKey: 'NLSChartDatasetForms',
  fields: [
    { name: 'form', labelKey: 'NLSChartFieldForm', kind: 'dimension', type: 'string' },
    { name: 'questionAnswer', labelKey: 'NLSChartFieldQuestionAnswer', kind: 'dimension', type: 'string' },
    { name: 'submitter', labelKey: 'NLSChartFieldSubmitter', kind: 'dimension', type: 'string' },
    { name: 'person', labelKey: 'NLSChartFieldPerson', kind: 'dimension', type: 'string' },
    { name: 'responses', labelKey: 'NLSChartFieldResponses', kind: 'measure', type: 'number' },
    { name: 'numberField', labelKey: 'NLSChartFieldNumberField', kind: 'measure', type: 'number' },
    { name: 'submittedAt', labelKey: 'NLSChartFieldSubmittedAt', kind: 'time', type: 'date' },
  ],
  defaultMeasure: { field: 'responses', agg: 'count' },
  defaultTimeField: 'submittedAt',
  allowedRoles: ['OWNER', 'ADMIN', 'EDITOR', 'VIEWER'],
  available: true,
}

function toDateSafe(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (val as { toDate: () => Date }).toDate()
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  if (typeof (val as { toMillis?: () => number }).toMillis === 'function') {
    try {
      const ms = (val as { toMillis: () => number }).toMillis()
      const d = new Date(ms)
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  if (typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  if (typeof val === 'string') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

export interface FormFieldOptionSummary {
  id?: string
  label?: string
  value?: string
}

export interface FormFieldSummary {
  id: string
  type: string
  label?: string
  allowMultiple?: boolean
  options?: Array<FormFieldOptionSummary | string>
}

export interface FormDefinitionSummary {
  id: string
  workspaceId?: string
  fields?: FormFieldSummary[]
}

export interface BuildFormRowOptions {
  person?: string
  questionAnswer?: string
  formDef?: FormDefinitionSummary | null
  spec?: {
    breakdown?: { field?: string; questionFieldId?: string }
    filters?: Array<{ field?: string; op?: string; value?: unknown }>
    measure?: { field?: string; questionFieldId?: string }
    secondMeasure?: { field?: string; questionFieldId?: string }
  } | null
  breakdown?: { field?: string; questionFieldId?: string }
  measure?: { field?: string; questionFieldId?: string }
  secondMeasure?: { field?: string; questionFieldId?: string }
}

function resolveOptionLabel(val: unknown, options?: Array<FormFieldOptionSummary | string>): string {
  if (val == null) return ''
  const strVal = String(val)
  if (!options || options.length === 0) return strVal
  for (const opt of options) {
    if (typeof opt === 'string') {
      if (opt === strVal) return opt
    } else if (opt && typeof opt === 'object') {
      if (opt.value === strVal || opt.id === strVal || opt.label === strVal) {
        return opt.label ?? opt.value ?? opt.id ?? strVal
      }
    }
  }
  return strVal
}

function resolveTargetQuestionField(
  formDef: FormDefinitionSummary | null | undefined,
  spec: { breakdown?: { field?: string; questionFieldId?: string } } | null | undefined,
): FormFieldSummary | null {
  if (!formDef || !Array.isArray(formDef.fields)) return null
  const questionFieldId = spec?.breakdown?.field === 'questionAnswer'
    ? spec.breakdown.questionFieldId
    : undefined

  if (questionFieldId) {
    const matched = formDef.fields.find((f) => f && f.id === questionFieldId)
    if (matched) return matched
  }

  // Default: first DROPDOWN field (never CHECKBOX)
  const firstDropdown = formDef.fields.find((f) => f && f.type === 'DROPDOWN')
  return firstDropdown ?? null
}

function resolveTargetNumberField(
  formDef: FormDefinitionSummary | null | undefined,
  spec: {
    measure?: { field?: string; questionFieldId?: string }
    secondMeasure?: { field?: string; questionFieldId?: string }
  } | null | undefined,
): FormFieldSummary | null {
  if (!formDef || !Array.isArray(formDef.fields)) return null
  const measureNumberId = spec?.measure?.field === 'numberField'
    ? spec.measure.questionFieldId
    : undefined
  const secondMeasureNumberId = spec?.secondMeasure?.field === 'numberField'
    ? spec.secondMeasure.questionFieldId
    : undefined
  const questionFieldId = measureNumberId || secondMeasureNumberId

  if (questionFieldId) {
    const matched = formDef.fields.find((f) => f && f.id === questionFieldId)
    if (matched) return matched
  }

  // Default: first NUMBER field
  const firstNum = formDef.fields.find((f) => f && (f.type === 'NUMBER' || f.type === 'number'))
  return firstNum ?? null
}

function resolveQuestionAnswer(
  answers: Record<string, unknown>,
  formDef?: FormDefinitionSummary | null,
  spec?: { breakdown?: { field?: string; questionFieldId?: string } } | null,
): string {
  const targetField = resolveTargetQuestionField(formDef, spec)
  if (!targetField) return ''

  const val = answers[targetField.id]
  if (val === undefined || val === null) return ''

  if (targetField.type === 'CHECKBOX') {
    return val === true ? 'true' : 'false'
  }

  if (Array.isArray(val)) {
    const labels = val.map((item) => resolveOptionLabel(item, targetField.options)).filter(Boolean)
    return labels.join(', ')
  }

  if (typeof val === 'boolean') {
    return val ? 'true' : 'false'
  }

  return resolveOptionLabel(val, targetField.options)
}

function resolveNumberField(
  answers: Record<string, unknown>,
  formDef?: FormDefinitionSummary | null,
  spec?: {
    measure?: { field?: string; questionFieldId?: string }
    secondMeasure?: { field?: string; questionFieldId?: string }
  } | null,
): number {
  const targetField = resolveTargetNumberField(formDef, spec)
  if (!targetField) return 0

  const val = answers[targetField.id]
  if (typeof val === 'number') return isNaN(val) ? 0 : val
  if (typeof val === 'string' && val.trim() && !isNaN(Number(val))) return Number(val)
  return 0
}

export function buildFormRow(
  data: Record<string, unknown>,
  id: string,
  options?: BuildFormRowOptions | string,
): Record<string, unknown> {
  const opts: BuildFormRowOptions = typeof options === 'string'
    ? { person: options }
    : (options ?? {})

  const submittedAt = toDateSafe(data.submittedAt) ?? toDateSafe(data.createdAt)
  const answers = (data.data && typeof data.data === 'object' && !Array.isArray(data.data))
    ? (data.data as Record<string, unknown>)
    : data

  let person = ''
  if (typeof opts.person === 'string') {
    person = opts.person
  } else if (opts.formDef) {
    const personFields = (Array.isArray(opts.formDef.fields)
      ? opts.formDef.fields
      : []
    ).filter((f) => f && f.type === 'PERSON')

    for (const pf of personFields) {
      const val = answers[pf.id]
      if (typeof val === 'string' && val.trim()) {
        person = val.trim()
        break
      } else if (Array.isArray(val)) {
        const found = val.find((v) => typeof v === 'string' && v.trim())
        if (found) {
          person = String(found).trim()
          break
        }
      }
    }
  }

  const effectiveSpec = opts.spec ?? {
    breakdown: opts.breakdown,
    measure: opts.measure,
    secondMeasure: opts.secondMeasure,
  }

  const questionAnswer = typeof opts.questionAnswer === 'string'
    ? opts.questionAnswer
    : resolveQuestionAnswer(answers, opts.formDef, effectiveSpec)
  const numberField = resolveNumberField(answers, opts.formDef, effectiveSpec)

  const submitter =
    typeof data.submittedBy === 'string' && data.submittedBy.trim()
      ? data.submittedBy.trim()
      : typeof data.submitterUid === 'string' && data.submitterUid.trim()
        ? data.submitterUid.trim()
        : typeof data.submitter === 'string' && data.submitter.trim()
          ? data.submitter.trim()
          : ''

  return {
    id,
    _id: id,
    form: typeof data.formId === 'string' ? data.formId : (typeof data.formTitle === 'string' ? data.formTitle : ''),
    questionAnswer,
    submitter,
    person,
    responses: 1,
    numberField,
    submittedAt: submittedAt ? submittedAt.toISOString() : null,
    title: typeof data.formTitle === 'string' ? data.formTitle : (typeof data.title === 'string' ? data.title : ''),
  }
}
